import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, TreePine, Users, Gift, ImageDown, MapPin, CalendarDays, MessageCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ImageLightbox } from '../components/ImageLightbox';

export const WHATSAPP_NUMBER = '351913446144';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

const stagger = {
  container: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12 } }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }
};

export default function ChristmasSessions() {
  const { t, language } = useLanguage();
  const c = t.christmas;

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(c.whatsappMessage)}`;

  // Per-route SEO: title, description and canonical (restored on unmount)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = c.seo.title;

    const meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    meta?.setAttribute('content', c.seo.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href');
    canonical?.setAttribute('href', 'https://www.verahilariofotografia.com/sessoes-de-natal');

    return () => {
      document.title = prevTitle;
      if (prevDescription) meta?.setAttribute('content', prevDescription);
      if (prevCanonical) canonical?.setAttribute('href', prevCanonical);
    };
  }, [c]);

  const highlights = [
    { icon: TreePine, data: c.highlights.scenario },
    { icon: Users, data: c.highlights.families },
    { icon: Gift, data: c.highlights.gift },
    { icon: ImageDown, data: c.highlights.delivery },
  ];

  return (
    <div data-testid="christmas-page" className="bg-warm-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#13241B] text-warm-50">
        {/* Soft festive glows */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-accent/20 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 bg-[#7B2D26]/30 rounded-full blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 text-accent-light font-medium text-sm tracking-[0.25em] uppercase mb-5">
                <Sparkles className="w-4 h-4" aria-hidden="true" />
                {c.eyebrow}
              </span>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight mb-4">
                {c.title}
                <br />
                <span className="text-accent-light">{c.titleAccent}</span>
              </h1>

              <p className="font-body text-lg text-warm-200 italic mb-6">{c.tagline}</p>

              <p className="text-warm-100/80 leading-relaxed mb-8 max-w-lg">{c.intro}</p>

              {/* Date + limited spots */}
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 text-base">
                  <CalendarDays className="w-4 h-4 text-accent-light" aria-hidden="true" />
                  <span>{c.startsLabel} <strong className="font-semibold">{c.startsDate}</strong></span>
                </div>
                <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 rounded-full px-5 py-2.5 text-base text-accent-light font-medium">
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  {c.limitedBadge}
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="christmas-cta-whatsapp">
                  <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg">
                    <MessageCircle className="mr-2 w-4 h-4" aria-hidden="true" />
                    {c.bookWhatsapp}
                  </Button>
                </a>
                <Link to="/contact" data-testid="christmas-cta-contact">
                  <Button
                    variant="outline"
                    className="border-2 border-warm-50/40 bg-transparent text-warm-50 hover:bg-warm-50 hover:text-warm-900 px-8 py-3 rounded-full transition-all duration-300"
                  >
                    {c.bookForm}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Poster */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative max-w-md mx-auto lg:ml-auto"
            >
              <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-3xl" aria-hidden="true" />
              <ImageLightbox
                src="/images/natal-estudio.jpg"
                alt={language === 'pt'
                  ? 'Cenário das sessões de Natal em estúdio: árvore iluminada, brinquedos de madeira e lanterna com velas'
                  : 'Christmas studio session set: glowing tree, wooden toys and a candle lantern'}
                width="1080"
                height="1350"
                className="relative rounded-2xl shadow-2xl w-full"
                openLabel={c.zoomImage}
                closeLabel={c.closeImage}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-14">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {c.eyebrow}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900">
              {c.highlights.title}
            </h2>
          </motion.div>

          <motion.div
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {highlights.map(({ icon: Icon, data }, i) => (
              <motion.div
                key={i}
                variants={stagger.item}
                className="bg-warm-50 rounded-2xl p-7 hover:shadow-xl transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-full bg-accent/10 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-accent" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-warm-900 mb-2">{data.title}</h3>
                <p className="text-warm-600 text-base leading-relaxed">{data.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28 bg-warm-100">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900">
              {c.details.title}
            </h2>
          </motion.div>

          <motion.ol
            variants={stagger.container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {c.details.items.map((item, i) => (
              <motion.li
                key={i}
                variants={stagger.item}
                className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-accent text-white text-sm font-semibold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-warm-700 leading-relaxed">{item}</p>
              </motion.li>
            ))}
          </motion.ol>

          <motion.div {...fadeUp} className="flex items-center justify-center gap-2 mt-10 text-warm-600 text-base">
            <MapPin className="w-4 h-4 text-accent" aria-hidden="true" />
            <span>Atalaia, Montijo · Setúbal, Portugal</span>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-900">
              {c.faq.title}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {c.faq.items.map((item, i) => (
              <motion.div {...fadeUp} key={i} className="border-b border-warm-200 pb-6">
                <h3 className="font-heading font-semibold text-lg text-warm-900 mb-2">{item.q}</h3>
                <p className="text-warm-600 leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 bg-[#13241B] relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] bg-accent/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <Sparkles className="w-7 h-7 text-accent-light mx-auto mb-6" aria-hidden="true" />
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-warm-50 mb-4">
              {c.limitedBadge}
            </h2>
            <p className="text-warm-200 text-lg leading-relaxed mb-8">{c.limitedNote}</p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="christmas-cta-whatsapp-bottom">
              <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-10 py-4 rounded-full transition-all duration-300 hover:shadow-lg text-base">
                <MessageCircle className="mr-2 w-4 h-4" aria-hidden="true" />
                {c.bookWhatsapp} · 913 446 144
                <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
